/**
 * Central frontend capability evaluation (Epic 22).
 * Backend authorization remains authoritative; this layer drives nav, routes, and UI affordances only.
 */
import { normalizeRole, type Role } from '../utils/roles';

export type AccessUserLike =
  | {
      role?: string | null;
      capabilities?: string[] | null;
      primary_profile?: string | null;
      access_posture?: string | null;
    }
  | null
  | undefined;
export type WarehouseSurfaceAccessKey =
  | 'overview'
  | 'liveOps'
  | 'incidents'
  | 'accuracy'
  | 'liveView'
  | 'liveAlerts'
  | 'analytics'
  | 'audit'
  | 'odoo'
  | 'setupRollout'
  | 'bringup'
  | 'setupWizard'
  | 'preflight'
  | 'devices'
  | 'deviceControl'
  | 'cameras'
  | 'zones'
  | 'policies'
  | 'blueprints'
  | 'configSnapshots'
  | 'agents'
  | 'configurations'
  | 'deployments'
  | 'diagnostics'
  | 'grafana'
  | 'opsRunbooks'
  | 'opsCopilot'
  | 'opsDr'
  | 'acceptance'
  | 'users'
  | 'accessScope';

type WarehouseSurfaceAccessRule = {
  label: string;
  globallyVisible?: boolean;
  check: (capabilities: string[]) => boolean;
};

const SAFETY_WORKFLOW_CAPABILITIES = [
  'safety_case:view',
  'safety_case:view_assigned',
  'safety_case:view_selected',
  'incidents:read',
] as const;

const INTERNAL_OPS_CAPABILITIES = ['support:impersonate', 'logs:view'] as const;

const EPIC_SETUP_READ_CAPABILITIES = [
  'camera:view',
  'zone:view',
  'policy:view',
  'diagnostics:view',
  'pipeline_status:view',
] as const;

export function getCapabilitiesFromUser(user: AccessUserLike): string[] {
  return Array.isArray(user?.capabilities)
    ? user!.capabilities.filter((value): value is string => typeof value === 'string')
    : [];
}

export function hasCapability(capabilities: string[], capability: string): boolean {
  if (!capability) return false;
  if (capability.endsWith('*')) {
    const prefix = capability.slice(0, -1);
    return capabilities.some((item) => item.startsWith(prefix));
  }
  return capabilities.includes(capability);
}

export function hasAnyCapability(capabilities: string[], capabilityList: string[]): boolean {
  return capabilityList.some((capability) => hasCapability(capabilities, capability));
}

/** Safety Case / Safety Reports workflow access (EPIC-161 caps or legacy incidents:read). */
export function hasSafetyWorkflowAccess(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, [...SAFETY_WORKFLOW_CAPABILITIES]);
}

/** External reviewer posture: selected-review caps without broader safety workflow access. */
export function isExternalReviewOnlyAccess(capabilities: string[]): boolean {
  if (isProductEvaluatorCapabilities(capabilities)) return false;
  const hasSelected = hasCapability(capabilities, 'safety_case:view_selected');
  const hasBroadSafety = hasAnyCapability(capabilities, [
    'safety_case:view',
    'safety_case:view_assigned',
    'incidents:read',
  ]);
  return hasSelected && !hasBroadSafety;
}

/** EPIC-165: evaluation-scoped read-only warehouse posture (not external /review-only). */
export function isProductEvaluatorCapabilities(capabilities: string[]): boolean {
  const hasCoreCaps =
    hasCapability(capabilities, 'live_view:view') &&
    hasCapability(capabilities, 'safety_case:view_selected') &&
    hasCapability(capabilities, 'zone:view') &&
    hasCapability(capabilities, 'policy:view') &&
    hasCapability(capabilities, 'incidents:read');
  const lacksElevated =
    !hasCapability(capabilities, 'safety_case:view') && !hasCapability(capabilities, 'zone:manage');
  return hasCoreCaps && lacksElevated;
}

/** EPIC-172: bounded WorkSafeBC sandbox — EHS workflow write, limited zone/policy edit, no admin. */
export function isSandboxEhsEvaluatorCapabilities(capabilities: string[]): boolean {
  const hasCore =
    hasCapability(capabilities, 'live_view:view') &&
    hasCapability(capabilities, 'safety_case:view') &&
    hasCapability(capabilities, 'zone:view') &&
    hasCapability(capabilities, 'policy:view') &&
    hasCapability(capabilities, 'incidents:read');
  const hasSandboxWrites = hasAnyCapability(capabilities, [
    'zone:manage',
    'policy:manage',
    'safety_case:create',
    'safety_case:update',
  ]);
  const lacksElevated =
    !hasCapability(capabilities, 'camera:manage') &&
    !hasCapability(capabilities, 'tenant:manage') &&
    !hasCapability(capabilities, 'users:invite') &&
    !hasCapability(capabilities, 'diagnostics:view');
  return hasCore && hasSandboxWrites && lacksElevated;
}

/** EPIC-166: explicit backend posture, with EPIC-161 primary_profile login hydration fallback. */
export function getAccessPosture(user: AccessUserLike): string | null {
  const posture = String(user?.access_posture || '').trim();
  if (posture) return posture;
  const profile = String(user?.primary_profile || '').trim();
  if (profile === 'sandbox_ehs_evaluator') return 'sandbox_ehs_evaluator';
  if (profile === 'product_evaluator') return 'product_evaluator';
  return null;
}

export function isSandboxEhsEvaluator(user: AccessUserLike): boolean {
  if (getAccessPosture(user) === 'sandbox_ehs_evaluator') return true;
  const profile = String(user?.primary_profile || '').trim();
  if (profile === 'sandbox_ehs_evaluator') return true;
  return isSandboxEhsEvaluatorCapabilities(getCapabilitiesFromUser(user));
}

export function isProductEvaluator(user: AccessUserLike): boolean {
  if (isSandboxEhsEvaluator(user)) return false;
  if (getAccessPosture(user) === 'product_evaluator') return true;
  return isProductEvaluatorCapabilities(getCapabilitiesFromUser(user));
}

/** EPIC-165/172: warehouse users on disposable demo evaluation tenants. */
export function isBoundedEvaluationWarehouseUser(user: AccessUserLike): boolean {
  return isProductEvaluator(user) || isSandboxEhsEvaluator(user);
}

/** Warehouse surfaces visible in the product evaluator restricted nav. */
export const PRODUCT_EVALUATOR_NAV_SURFACES: WarehouseSurfaceAccessKey[] = [
  'liveView',
  'cameras',
  'zones',
  'policies',
  'incidents',
  'acceptance',
];

/** EPIC-165 read-only warehouse posture — product evaluators only (not all non-writers). */
export function isReadOnlyEvaluator(
  user: AccessUserLike,
  _surface?: WarehouseSurfaceAccessKey,
): boolean {
  return isProductEvaluator(user);
}

/** Edgentik internal/debug/support surfaces — not customer_admin safe. */
export function hasInternalSupportAccess(capabilities: string[]): boolean {
  return (
    hasCapability(capabilities, 'admin:*') ||
    hasAnyCapability(capabilities, [...INTERNAL_OPS_CAPABILITIES])
  );
}

/** Eligible to see persona preview controls (real authenticated user only). */
export function canUsePersonaPreviewControls(user: AccessUserLike): boolean {
  if (!user) return false;
  return hasInternalSupportAccess(getCapabilitiesFromUser(user));
}

/** Composite: Acceptance nav entry visibility (cross-lane read). */
export function canViewAcceptanceNav(capabilities: string[]): boolean {
  return hasSafetyWorkflowAccess(capabilities);
}

export function canOpenAcceptanceReportFlow(capabilities: string[]): boolean {
  return hasSafetyWorkflowAccess(capabilities);
}

export function canManageAcceptanceArtifacts(capabilities: string[]): boolean {
  return (
    canOpenAcceptanceReportFlow(capabilities) &&
    (hasCapability(capabilities, 'admin:*') ||
      hasAnyCapability(capabilities, [
        'safety_case:create',
        'safety_case:update',
        'devices:write',
        'deployments:write',
        'policies:write',
      ]))
  );
}

/** Dedicated B1 sign capability. Not Case UPDATE. Backend: POST /reports/:id/finalize (signed_scan:sign). */
export function canSignSafetyScan(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'signed_scan:sign')
    && !isReadOnlyEvaluator(user, 'acceptance');
}

/** Acceptance / Safety Case mutation affordances (excludes read-only evaluators). */
export function canManageSafetyCaseArtifacts(user: AccessUserLike): boolean {
  const caps = getCapabilitiesFromUser(user);
  return canManageAcceptanceArtifacts(caps) && !isReadOnlyEvaluator(user, 'acceptance');
}

/** Case-native owner assign/clear. Backend: PATCH /safety-cases/:id/owner (safety_case:assign). */
export function canAssignSafetyCaseOwner(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'safety_case:assign')
    && !isReadOnlyEvaluator(user, 'acceptance');
}

export function canDownloadInternalPackage(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'package:download_internal');
}

export function canDownloadCustomerPackage(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'package:download_customer');
}

export function canViewTechnicalAppendix(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'technical_appendix:view');
}

export function canDownloadExternalSafePackage(user: AccessUserLike): boolean {
  return hasCapability(getCapabilitiesFromUser(user), 'package:download_external_safe');
}

/** External-safe PDF export for bounded evaluation accounts (ZIP-only posture otherwise). */
export function canDownloadExternalSafeReportPdf(user: AccessUserLike): boolean {
  return (
    (isProductEvaluator(user) || isSandboxEhsEvaluator(user)) &&
    canDownloadExternalSafePackage(user)
  );
}

/** Full PDF/DOCX/CSV export card (customer package roles). */
export function canDownloadReportExportFormats(user: AccessUserLike): boolean {
  return canDownloadCustomerPackage(user) || canDownloadExternalSafeReportPdf(user);
}

export function canDownloadReportArtifactDocxOrCsv(user: AccessUserLike): boolean {
  return canDownloadCustomerPackage(user);
}

export function canInviteUsers(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, ['admin:users:manage', 'users:invite']);
}

export function canAssignRoles(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, ['admin:rbac:manage', 'roles:assign']);
}

export function canManageUsers(capabilities: string[]): boolean {
  return canInviteUsers(capabilities);
}

export function canManageAccessScope(capabilities: string[]): boolean {
  return canAssignRoles(capabilities);
}

export function canManageIncidents(capabilities: string[]): boolean {
  return hasCapability(capabilities, 'admin:*') || hasCapability(capabilities, 'incidents:write');
}

export function canUseIncidentOpsAutomation(capabilities: string[]): boolean {
  return canManageIncidents(capabilities) || hasCapability(capabilities, 'audit:write');
}

export function canOpenIncidentsArtifactEntry(capabilities: string[]): boolean {
  return hasCapability(capabilities, 'incidents:read') || canManageAcceptanceArtifacts(capabilities);
}

export function hasSetupReadAccess(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, [
    'devices:read',
    'deployments:read',
    'cameras:read',
    'sites:read',
    'policies:read',
    'blueprints:read',
    ...EPIC_SETUP_READ_CAPABILITIES,
  ]);
}

export function hasSetupWriteAccess(capabilities: string[]): boolean {
  return (
    hasCapability(capabilities, 'admin:*') ||
    hasAnyCapability(capabilities, [
      'devices:write',
      'deployments:write',
      'policies:write',
      'camera:manage',
      'zone:manage',
      'policy:manage',
    ])
  );
}

/** Policy create/edit: Epic 161 `policy:manage` or legacy `policies:write`. */
export function canManagePolicies(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, ['policies:write', 'policy:manage']);
}

/** Policy activate/deploy: same canonical write capability as create/edit. */
export function canDeployPolicies(capabilities: string[]): boolean {
  return canManagePolicies(capabilities);
}

export function hasLiveOpsNavAccess(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, ['incidents:read', 'audit:read', 'live_view:view']);
}

export function hasHealthNavAccess(capabilities: string[]): boolean {
  return hasAnyCapability(capabilities, [
    'devices:read',
    'deployments:read',
    'audit:read',
    'diagnostics:view',
    'pipeline_status:view',
  ]);
}

const WAREHOUSE_SURFACE_ACCESS_RULES: Record<WarehouseSurfaceAccessKey, WarehouseSurfaceAccessRule> = {
  overview: {
    label: 'Authenticated warehouse shell access',
    globallyVisible: true,
    check: () => true,
  },
  liveOps: {
    label: 'incidents:read, audit:read, or live_view:view',
    check: (capabilities) => hasLiveOpsNavAccess(capabilities),
  },
  incidents: {
    label: 'incidents:read',
    check: (capabilities) => hasCapability(capabilities, 'incidents:read'),
  },
  accuracy: {
    label: 'incidents:read or metrics:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['incidents:read', 'metrics:read']),
  },
  liveView: {
    label: 'live_view:view or incidents:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['live_view:view', 'incidents:read']),
  },
  liveAlerts: {
    label: 'incidents:read',
    check: (capabilities) => hasCapability(capabilities, 'incidents:read'),
  },
  analytics: {
    label: 'incidents:read or audit:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['incidents:read', 'audit:read']),
  },
  audit: {
    label: 'audit:read',
    check: (capabilities) => hasCapability(capabilities, 'audit:read'),
  },
  odoo: {
    label: 'incidents:read',
    check: (capabilities) => hasCapability(capabilities, 'incidents:read'),
  },
  setupRollout: {
    label: 'setup read or EPIC-161 configure capabilities',
    check: (capabilities) => hasSetupReadAccess(capabilities),
  },
  bringup: {
    label: 'devices:read',
    check: (capabilities) => hasCapability(capabilities, 'devices:read'),
  },
  setupWizard: {
    label: 'devices:read',
    check: (capabilities) => hasCapability(capabilities, 'devices:read'),
  },
  preflight: {
    label: 'devices:read or deployments:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['devices:read', 'deployments:read']),
  },
  devices: {
    label: 'devices:read',
    check: (capabilities) => hasCapability(capabilities, 'devices:read'),
  },
  deviceControl: {
    label: 'devices:write',
    check: (capabilities) => hasCapability(capabilities, 'devices:write'),
  },
  cameras: {
    label: 'camera:view or cameras:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['camera:view', 'cameras:read']),
  },
  zones: {
    label: 'zone:view, sites:read, devices:read, or cameras:read',
    check: (capabilities) =>
      hasAnyCapability(capabilities, ['zone:view', 'sites:read', 'devices:read', 'cameras:read']),
  },
  policies: {
    label: 'policy:view or policies:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['policy:view', 'policies:read']),
  },
  blueprints: {
    label: 'blueprints:read',
    check: (capabilities) => hasCapability(capabilities, 'blueprints:read'),
  },
  configSnapshots: {
    label: 'blueprints:read',
    check: (capabilities) => hasCapability(capabilities, 'blueprints:read'),
  },
  agents: {
    label: 'devices:read',
    check: (capabilities) => hasCapability(capabilities, 'devices:read'),
  },
  configurations: {
    label: 'deployments:read',
    check: (capabilities) => hasCapability(capabilities, 'deployments:read'),
  },
  deployments: {
    label: 'deployments:read',
    check: (capabilities) => hasCapability(capabilities, 'deployments:read'),
  },
  diagnostics: {
    label: 'diagnostics:view, devices:read, or deployments:read',
    check: (capabilities) =>
      hasAnyCapability(capabilities, ['diagnostics:view', 'devices:read', 'deployments:read']),
  },
  grafana: {
    label: 'devices:read or deployments:read',
    check: (capabilities) => hasAnyCapability(capabilities, ['devices:read', 'deployments:read']),
  },
  opsRunbooks: {
    label: 'support:impersonate, logs:view, or audit:read',
    check: (capabilities) =>
      hasInternalSupportAccess(capabilities) || hasCapability(capabilities, 'audit:read'),
  },
  opsCopilot: {
    label: 'support:impersonate, logs:view, or audit:read',
    check: (capabilities) =>
      hasInternalSupportAccess(capabilities) || hasCapability(capabilities, 'audit:read'),
  },
  opsDr: {
    label: 'support:impersonate or logs:view',
    check: (capabilities) => hasInternalSupportAccess(capabilities),
  },
  acceptance: {
    label: 'safety workflow or incidents:read',
    check: (capabilities) => canViewAcceptanceNav(capabilities),
  },
  users: {
    label: 'admin:users:manage or users:invite',
    check: (capabilities) => canInviteUsers(capabilities),
  },
  accessScope: {
    label: 'admin:rbac:manage or roles:assign',
    check: (capabilities) => canAssignRoles(capabilities),
  },
};

export function canAccessWarehouseSurface(capabilities: string[], surface: WarehouseSurfaceAccessKey): boolean {
  return WAREHOUSE_SURFACE_ACCESS_RULES[surface].check(capabilities);
}

export function getWarehouseSurfaceRequirementLabel(surface: WarehouseSurfaceAccessKey): string {
  return WAREHOUSE_SURFACE_ACCESS_RULES[surface].label;
}

export function isWarehouseSurfaceGloballyVisible(surface: WarehouseSurfaceAccessKey): boolean {
  return Boolean(WAREHOUSE_SURFACE_ACCESS_RULES[surface].globallyVisible);
}

export function getGloballyVisibleWarehouseSurfaces(): WarehouseSurfaceAccessKey[] {
  return (Object.entries(WAREHOUSE_SURFACE_ACCESS_RULES) as Array<[WarehouseSurfaceAccessKey, WarehouseSurfaceAccessRule]>)
    .filter(([, rule]) => rule.globallyVisible)
    .map(([surface]) => surface);
}

/**
 * Advanced incidents mode exposes tooling-oriented UX and should stay write/ops oriented.
 * Read-only policy or blueprint capabilities alone should not unlock it.
 */
export function canUseAdvancedIncidentsUi(capabilities: string[]): boolean {
  return (
    hasCapability(capabilities, 'admin:*') ||
    hasSetupWriteAccess(capabilities) ||
    hasAnyCapability(capabilities, ['audit:write', 'incidents:write'])
  );
}

/** Lightweight hint for telemetry or future defaults; not used for gating. */
export function getPrimaryPersonaLikeSurface(capabilities: string[]): string {
  if (hasInternalSupportAccess(capabilities) && canManageUsers(capabilities)) return 'access_admin';
  if (canManageAcceptanceArtifacts(capabilities)) return 'acceptance_manager';
  if (hasSetupWriteAccess(capabilities)) return 'setup_writer';
  if (hasSetupReadAccess(capabilities)) return 'setup_reader';
  if (hasSafetyWorkflowAccess(capabilities)) return 'live_ops';
  return 'viewer';
}

export function getCanonicalRole(user: AccessUserLike): Role {
  return normalizeRole(user?.role);
}

/** EPIC-160 UX: external_reviewer-only accounts (EPIC-161 selected-scope caps). */
export function isExternalSafetyReviewer(user: AccessUserLike): boolean {
  if (isProductEvaluator(user) || isSandboxEhsEvaluator(user)) return false;
  const caps = getCapabilitiesFromUser(user);
  if (isExternalReviewOnlyAccess(caps)) return true;
  // Login hydrates primary_profile before capabilities; still route external-only accounts correctly.
  const profile = String(user?.primary_profile || '').trim();
  return profile === 'external_reviewer' && !hasCapability(caps, 'incidents:read');
}

/**
 * External reviewers use a dedicated review route only — not the warehouse shell.
 * Founder/admin demo mode retains full warehouse access via non-reviewer capabilities.
 */
export function canAccessExternalSafetyReview(user: AccessUserLike): boolean {
  return isExternalSafetyReviewer(user);
}

/** Warehouse surfaces are denied when the user is external-reviewer-only. */
export function canAccessWarehouseSurfaceForUser(
  user: AccessUserLike,
  surface: WarehouseSurfaceAccessKey,
): boolean {
  const caps = getCapabilitiesFromUser(user);
  if (isBoundedEvaluationWarehouseUser(user)) {
    if (!PRODUCT_EVALUATOR_NAV_SURFACES.includes(surface)) {
      return false;
    }
    return canAccessWarehouseSurface(caps, surface);
  }
  if (isExternalSafetyReviewer(user) && !hasCapability(caps, 'incidents:read')) {
    return false;
  }
  return canAccessWarehouseSurface(caps, surface);
}
